/**
 * In-memory fake of the RPC / edge-function layer (VITE_MOCK=1). Behaviour follows the contract
 * closely enough to demo every flow: Mode A/B quotes, submit → review, spend order, audience
 * minimums, redeem codes, realtime lead events. State lives for the page session only.
 */
import { ApiError, type Api, type Session } from '../api';
import type * as T from '../types';
import { AIRCRAFT_KINDS } from '../types';
import { ATTRIBUTE_DEFS, defsFor } from './attributeDefs';
import * as S from './seed';
import { toJpeg } from '@/lib/image';
import { parseCsv } from '@/lib/csv';

/** Demo starts signed in; "Sign out" sticks for the tab (sessionStorage) so /login is demoable. */
const SIGNED_OUT_KEY = 'flyt.mock.signedOut';
function startSignedOut(): boolean {
  try { return sessionStorage.getItem(SIGNED_OUT_KEY) === '1'; } catch { return false; }
}
function rememberSignedOut(out: boolean) {
  try { if (out) sessionStorage.setItem(SIGNED_OUT_KEY, '1'); else sessionStorage.removeItem(SIGNED_OUT_KEY); } catch { /* ignore */ }
}

const db = {
  me: S.ME,
  session: (startSignedOut() ? null : { user_id: S.U_ME, email: S.ME.email }) as Session | null,
  accounts: S.ACCOUNTS,
  roles: S.MY_ROLES as Record<string, T.MemberRole>,
  members: S.MEMBERS,
  listings: S.LISTINGS,
  documents: S.DOCUMENTS,
  priceChanges: S.PRICE_CHANGES,
  leads: S.LEADS,
  messages: S.MESSAGES,
  replies: S.SAVED_REPLIES,
  campaigns: S.CAMPAIGNS,
  stats: S.STATS,
  organic: S.ORGANIC,
  ledger: S.LEDGER,
  grants: S.GRANTS,
  programs: S.PROGRAMS,
  policies: S.POLICIES,
  feeds: S.FEEDS,
  runs: S.IMPORT_RUNS,
  apiKeys: S.API_KEYS,
  webhooks: S.WEBHOOKS,
  reviewRuns: S.REVIEW_RUNS,
  audit: S.AUDIT,
  settings: {} as Record<string, T.AccountSettings>,
};

const authListeners = new Set<(s: Session | null) => void>();
const leadListeners = new Map<string, Set<(e: { lead_id: string; kind: 'lead' | 'message' }) => void>>();

function delay<R>(v: R, ms = 160 + Math.random() * 220): Promise<R> {
  return new Promise((res) => setTimeout(() => res(structuredClone(v)), ms));
}
function fail(message: string, code = 'error', fields: T.FieldError[] = []): never {
  throw new ApiError(message, code, fields);
}
function uuid(): string {
  return crypto.randomUUID();
}
const now = () => new Date().toISOString();

function requireAuth() {
  if (!db.session) fail('Not signed in', 'auth');
}
function acct(id: string): T.Account {
  return db.accounts.find((a) => a.id === id) ?? fail('Account not found', 'not_found');
}
function policy(): T.PricingPolicy {
  const t = Date.now();
  return [...db.policies].filter((p) => Date.parse(p.effective_at) <= t).sort((a, b) => Date.parse(b.effective_at) - Date.parse(a.effective_at))[0];
}
function audit(action: string, target_type: string, target_id: string, detail: Record<string, unknown>) {
  db.audit.unshift({
    id: Math.max(0, ...db.audit.map((a) => a.id)) + 1, actor_id: db.me.id, actor_name: db.me.display_name, actor_kind: 'staff',
    action, target_type, target_id, detail, occurred_at: now(),
  });
}
function emitLead(accountId: string, lead_id: string, kind: 'lead' | 'message') {
  leadListeners.get(accountId)?.forEach((cb) => cb({ lead_id, kind }));
}

function balances(accountId: string) {
  const t = Date.now();
  const paid = db.ledger.filter((r) => r.account_id === accountId && r.funding === 'paid').reduce((a, r) => a + r.amount_cents, 0);
  const live = db.grants.filter((g) => g.account_id === accountId && Date.parse(g.expires_at) > t && g.remaining_cents > 0);
  const promo = live.reduce((a, g) => a + g.remaining_cents, 0);
  const next = live.map((g) => g.expires_at).sort()[0] ?? null;
  return { paid, promo, next, live };
}

function quoteFor(accountId: string, kind: T.ListingKind): T.Quote {
  const a = acct(accountId);
  const p = policy();
  const fee = p.listing_fee_cents_per_day[kind] ?? 10;
  if (p.mode === 'free_listing_paid_promotion') {
    return { mode: p.mode, basis: 'policy_free', fee_cents_per_day: 0, free_remaining: p.max_active_free_mode, free_listing_days: p.free_listing_days, requires_card: true, can_promote: true };
  }
  if (a.kind === 'individual') {
    const yearAgo = Date.now() - 365 * 86400000;
    const used = db.listings.filter((l) => l.account_id === accountId && l.billing_basis === 'free_annual' && Date.parse(l.published_at ?? l.created_at) > yearAgo).length;
    const remaining = Math.max(0, p.individual_free_listings_per_year - used);
    if (remaining > 0) return { mode: p.mode, basis: 'free_annual', fee_cents_per_day: 0, free_remaining: remaining, free_listing_days: p.free_listing_days, requires_card: true, can_promote: true };
    return { mode: p.mode, basis: 'paid', fee_cents_per_day: fee, free_remaining: 0, free_listing_days: p.free_listing_days, requires_card: true, can_promote: true };
  }
  return { mode: p.mode, basis: 'paid', fee_cents_per_day: fee, free_remaining: 0, free_listing_days: p.free_listing_days, requires_card: true, can_promote: true };
}

function validateListing(input: T.ListingInput): T.FieldError[] {
  const errs: T.FieldError[] = [];
  const defs = new Map(ATTRIBUTE_DEFS.map((d) => [d.key, d]));
  for (const [k, v] of Object.entries(input.attributes ?? {})) {
    const d = defs.get(k);
    if (!d) { errs.push({ field: `attributes.${k}`, message: 'Unknown attribute' }); continue; }
    if (v == null || v === '') continue;
    if (['number', 'integer', 'money', 'hours'].includes(d.data_type) && typeof v !== 'number') errs.push({ field: `attributes.${k}`, message: `${d.label} must be a number` });
    if (d.data_type === 'integer' && typeof v === 'number' && !Number.isInteger(v)) errs.push({ field: `attributes.${k}`, message: `${d.label} must be a whole number` });
    if (d.data_type === 'enum' && d.enum_values && !d.enum_values.includes(String(v))) errs.push({ field: `attributes.${k}`, message: `${d.label}: pick one of the listed values` });
  }
  if (input.year != null && (input.year < 1930 || input.year > new Date().getFullYear() + 1)) errs.push({ field: 'year', message: 'Year looks wrong' });
  if (input.price_cents != null && input.price_cents < 0) errs.push({ field: 'price_cents', message: 'Price cannot be negative' });
  return errs;
}

function missingFor(l: T.MyListing): string[] {
  const m: string[] = [];
  if (!l.title?.trim()) m.push('title');
  if (AIRCRAFT_KINDS.includes(l.kind)) {
    if (!l.make_id) m.push('make_id');
    if (!l.model_id) m.push('model_id');
    if (!l.year) m.push('year');
    if (!l.location_state && !l.location_ident) m.push('location_state');
  }
  if (!l.images.length) m.push('images');
  if (l.price_cents == null && !l.price_on_request) m.push('price_cents');
  return m;
}

function qualityScore(l: T.MyListing): number {
  const defs = defsFor(l.kind);
  const filled = defs.filter((d) => {
    const v = l.attributes[d.key];
    return v != null && v !== '' && !(Array.isArray(v) && v.length === 0);
  }).length;
  let s = Math.round((filled / Math.max(1, defs.length)) * 50);
  s += Math.min(25, l.images.length * 5);
  if (l.description && l.description.length > 120) s += 10;
  if (l.serial_number || l.registration) s += 10;
  if (l.registry_match === 'matched') s += 5;
  return Math.min(100, s);
}

/** Pretend mkt-review ran: a couple of seconds after submit, decide. */
function scheduleReview(id: string) {
  setTimeout(() => {
    const l = db.listings.find((x) => x.id === id);
    if (!l || l.state !== 'pending_review') return;
    const reasons: T.ReviewReason[] = [];
    if (l.price_cents != null && l.price_cents > 0 && l.price_cents < 5_000_000 && AIRCRAFT_KINDS.includes(l.kind)) {
      reasons.push({ code: 'price_suspicious', message: 'Price is far below comparable listings for this model. Double-check it, or explain in the description.', field: 'price_cents' });
    }
    if (/wire|western union|whatsapp/i.test(l.description ?? '')) {
      reasons.push({ code: 'off_platform_payment', message: 'Remove payment or contact instructions from the description — buyers contact you through LowOps.', field: 'description' });
    }
    const decision: T.ReviewStatus = reasons.some((r) => r.code === 'off_platform_payment') ? 'rejected' : reasons.length ? 'limited' : 'approved';
    l.review_status = decision;
    l.review_reasons = reasons;
    l.reviewed_at = now();
    l.registry_match = l.registration?.startsWith('N') ? 'matched' : l.registration ? 'n/a' : 'unchecked';
    l.quality_score = qualityScore(l);
    l.custom_attributes = l.custom_attributes.map((c) => (c.mapped_key ? c : { ...c, ...suggestMapping(c.label) }));
    if (decision === 'rejected') l.state = 'rejected';
    else {
      l.state = 'active';
      l.published_at = now();
      l.expires_at = l.billing_basis === 'free_annual' ? new Date(Date.now() + policy().free_listing_days * 86400000).toISOString() : null;
    }
    db.reviewRuns.unshift({
      id: Math.max(0, ...db.reviewRuns.map((r) => r.id)) + 1, target_type: 'listing', target_id: l.id, target_label: l.title,
      account_name: acct(l.account_id).display_name, checks: { attributes: 'ok', reasons: reasons.map((r) => r.code) }, decision, model: 'claude-haiku-4-5', occurred_at: now(),
    });
  }, 2500);
}

function suggestMapping(label: string): { mapped_key: string | null; confidence: number | null } {
  const norm = label.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  for (const d of ATTRIBUTE_DEFS) {
    const names = [d.label, d.key.replace(/_/g, ' '), ...d.synonyms].map((s) => s.toLowerCase());
    if (names.some((n) => n === norm)) return { mapped_key: d.key, confidence: 0.96 };
    if (names.some((n) => n.length > 2 && (norm.includes(n) || n.includes(norm)))) return { mapped_key: d.key, confidence: 0.72 };
  }
  return { mapped_key: null, confidence: null };
}

function inboxRow(l: T.Lead): T.InboxRow {
  const msgs = db.messages.filter((m) => m.lead_id === l.id).sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  const last = msgs[msgs.length - 1];
  const listing = db.listings.find((x) => x.id === l.listing_id);
  return {
    ...l,
    listing_title: listing?.title ?? null,
    listing_thumb: listing?.images[0] ?? null,
    last_message: last?.body ?? null,
    last_message_at: last?.created_at ?? null,
    last_sender_side: last?.sender_side ?? null,
    unread: msgs.filter((m) => m.sender_side === 'buyer' && !m.read_by_seller_at).length,
    flags: [...new Set(msgs.flatMap((m) => m.flags))],
  };
}

function audienceFor(t: T.Targeting): number {
  // A plausible pilot population (~38k on LowOps) narrowed by each key.
  let n = 38_000;
  const f = (arr: unknown[] | undefined, per: number, max = 1) => (arr && arr.length ? Math.min(max, arr.length * per) : 1);
  n *= f(t.credentials, 0.18);
  n *= f(t.type_ratings, 0.07);
  n *= f(t.hours_bands, 0.26);
  n *= f(t.segments, 0.11);
  n *= f(t.states, 0.024);
  if (t.in_market && ((t.in_market.kinds?.length ?? 0) + (t.in_market.make_ids?.length ?? 0) + (t.in_market.model_ids?.length ?? 0)) > 0) {
    n *= 0.09 * Math.min(1, (t.in_market.days ?? 30) / 30 + 0.2);
  }
  if (t.context && ((t.context.kinds?.length ?? 0) + (t.context.make_ids?.length ?? 0)) > 0) n *= 0.6;
  return Math.round(n);
}

const PERSONAL_KEYS: (keyof T.Targeting)[] = ['credentials', 'type_ratings', 'hours_bands', 'segments', 'states', 'in_market'];
function hasPersonal(t: T.Targeting): boolean {
  return PERSONAL_KEYS.some((k) => {
    const v = t[k];
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === 'object') return Object.values(v).some((x) => Array.isArray(x) && x.length > 0);
    return false;
  });
}


const CORE_FIELD_SYNONYMS: Record<string, string[]> = {
  external_id: ['stock', 'stock no', 'stockno', 'stock number', 'id', 'external id', 'sku'],
  title: ['title', 'name', 'headline'],
  kind: ['kind', 'type', 'category'],
  year: ['year', 'yr', 'model year'],
  make: ['make', 'manufacturer', 'mfr'],
  model: ['model'],
  serial_number: ['serial', 'serial number', 's/n', 'sn', 'msn'],
  registration: ['registration', 'reg', 'tail', 'n number', 'n-number', 'tail number'],
  price: ['price', 'asking', 'asking price', 'price usd'],
  description: ['description', 'desc', 'comments', 'remarks'],
  location_state: ['state', 'location state'],
  location_ident: ['airport', 'icao', 'location', 'base'],
  images: ['images', 'photos', 'image urls', 'photo urls'],
};
function suggestHeader(h: string): string | null {
  const norm = h.toLowerCase().replace(/[_]+/g, ' ').trim();
  for (const [k, syn] of Object.entries(CORE_FIELD_SYNONYMS)) if (syn.includes(norm)) return k;
  return suggestMapping(h).mapped_key;
}

const DEFAULT_SETTINGS: T.AccountSettings = {
  notifications: { new_lead_email: true, new_message_email: true, budget_low_email: true, listing_status_email: true },
  lead_routing: { mode: 'round_robin', fixed_user_id: null, sales_user_ids: [S.U_MARCO, S.U_PRIYA], auto_reply: 'Thanks for your interest — a member of our sales team will reply within the hour during business hours (MT).' },
};

function fakeUpload(file: File): { path: string; url: string } {
  return { path: `${db.me.id}/${uuid()}-${file.name}`, url: URL.createObjectURL(file) };
}

// Simulate a realtime inbound message ~25s after the inbox subscribes (once per session).
let simulatedInbound = false;

export function createMockApi(): Api {
  return {
    mode: 'mock',

    // ── Auth ──
    getSession: () => delay(db.session, 40),
    onAuthChange(cb) {
      authListeners.add(cb);
      return () => authListeners.delete(cb);
    },
    async signInWithPassword(email, password) {
      await delay(null, 400);
      if (!email.includes('@')) fail('Enter a valid email address', 'auth');
      if (password.length < 6) fail('Invalid login credentials', 'auth');
      db.session = { user_id: S.U_ME, email: db.me.email }; rememberSignedOut(false);
      authListeners.forEach((cb) => cb(db.session));
    },
    async signUp(email, password) {
      await delay(null, 500);
      if (!email.includes('@')) fail('Enter a valid email address', 'auth');
      if (password.length < 8) fail('Password should be at least 8 characters', 'auth');
      db.session = { user_id: S.U_ME, email: db.me.email }; rememberSignedOut(false);
      authListeners.forEach((cb) => cb(db.session));
      return { needsConfirmation: false };
    },
    async sendMagicLink(email) {
      await delay(null, 400);
      if (!email.includes('@')) fail('Enter a valid email address', 'auth');
    },
    async signOut() {
      await delay(null, 100);
      db.session = null; rememberSignedOut(true);
      authListeners.forEach((cb) => cb(null));
    },
    async me() {
      requireAuth();
      return delay(db.me);
    },

    // ── Accounts ──
    async myAccounts() {
      requireAuth();
      return delay(
        db.accounts.filter((a) => db.roles[a.id]).map((a) => ({ ...a, role: db.roles[a.id] })).sort((a, b) => (a.kind === 'dealer' ? -1 : 1) - (b.kind === 'dealer' ? -1 : 1)),
      );
    },
    async myIndividualAccount() {
      requireAuth();
      return delay(acct(S.A_ME));
    },
    async createDealerAccount(input) {
      requireAuth();
      if (!/^[a-z0-9-]{3,60}$/.test(input.slug)) fail('Storefront URL can use lowercase letters, numbers and dashes (3–60).', '22023', [{ field: 'slug', message: 'Invalid slug' }]);
      if (db.accounts.some((a) => a.slug === input.slug)) fail('That storefront URL is taken.', '23505', [{ field: 'slug', message: 'Taken' }]);
      const id = uuid();
      const base = acct(S.A_ME);
      db.accounts.push({
        ...base, id, kind: 'dealer', user_id: null, company_id: uuid(), display_name: input.display_name, slug: input.slug,
        card_ok: false, card_brand: null, card_last4: null, verified: false, identity_verified: false, domain_verified: false,
        storefront: { website: input.website || null }, created_at: now(), updated_at: now(), trust_tier: 0, daily_spend_cap_cents: 5000,
      });
      db.roles[id] = 'owner';
      db.members.push({ account_id: id, user_id: db.me.id, role: 'owner', status: 'active', invited_email: null, display_name: db.me.display_name, email: db.me.email, created_at: now() });
      return delay(id, 500);
    },
    async updateStorefront(accountId, storefront, displayName) {
      const a = acct(accountId);
      if (!displayName.trim()) fail('Display name is required', '22023', [{ field: 'display_name', message: 'Required' }]);
      if (storefront.website && !/^https?:\/\//.test(storefront.website)) fail('Website must start with https://', '22023', [{ field: 'website', message: 'Must start with https://' }]);
      a.storefront = storefront;
      a.display_name = displayName;
      a.updated_at = now();
      return delay(undefined);
    },
    members: (accountId) => delay(db.members.filter((m) => m.account_id === accountId && m.status !== 'removed')),
    async inviteMember(accountId, email, role) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) fail('Enter a valid email address', '22023', [{ field: 'email', message: 'Invalid email' }]);
      if (db.members.some((m) => m.account_id === accountId && m.email === email && m.status !== 'removed')) fail('That person is already on the team.', '23505');
      db.members.push({ account_id: accountId, user_id: null, role, status: 'invited', invited_email: email, email, display_name: null, created_at: now() });
      return delay(uuid());
    },
    async setMemberRole(accountId, userId, role) {
      const m = db.members.find((x) => x.account_id === accountId && (x.user_id === userId || x.invited_email === userId));
      if (!m) fail('Member not found', 'not_found');
      if (m.role === 'owner' && role !== 'owner' && db.members.filter((x) => x.account_id === accountId && x.role === 'owner' && x.status === 'active').length < 2) {
        fail('An account needs at least one owner.', 'P0001');
      }
      m.role = role;
      return delay(undefined);
    },
    async removeMember(accountId, userId) {
      const m = db.members.find((x) => x.account_id === accountId && (x.user_id === userId || x.invited_email === userId));
      if (!m) fail('Member not found', 'not_found');
      if (m.user_id === db.me.id) fail('You can’t remove yourself. Ask another owner.', 'P0001');
      m.status = 'removed';
      return delay(undefined);
    },
    async setInviteRole(accountId, email, role) {
      const m = db.members.find((x) => x.account_id === accountId && x.status === 'invited' && x.invited_email?.toLowerCase() === email.toLowerCase());
      if (!m) fail('Invite not found', 'not_found');
      m.role = role;
      return delay(undefined);
    },
    async cancelInvite(accountId, email) {
      const m = db.members.find((x) => x.account_id === accountId && x.status === 'invited' && x.invited_email?.toLowerCase() === email.toLowerCase());
      if (!m) fail('Invite not found', 'not_found');
      m.status = 'removed';
      return delay(undefined);
    },
    async setAutoRecharge(accountId, enabled, threshold, amount) {
      if (amount < 2500) fail('Recharge amount must be at least $25', '22023', [{ field: 'amount', message: 'Min $25' }]);
      const a = acct(accountId);
      a.auto_recharge_enabled = enabled;
      a.auto_recharge_threshold_cents = threshold;
      a.auto_recharge_amount_cents = amount;
      return delay(undefined);
    },
    getSettings: (accountId) => delay(db.settings[accountId] ?? DEFAULT_SETTINGS),
    async saveSettings(accountId, settings) {
      db.settings[accountId] = settings;
      return delay(undefined);
    },

    // ── Taxonomy ──
    attributeDefs: (kind) => delay(defsFor(kind), 120),
    makes: (cls) => {
      const ids = new Set(S.MODELS.filter((m) => !cls || m.aircraft_class === cls).map((m) => m.make_id));
      return delay(S.MAKES.filter((m) => ids.has(m.id)), 100);
    },
    models: (makeId) => delay(S.MODELS.filter((m) => m.make_id === makeId), 100),

    // ── Listings ──
    quote: (accountId, kind) => delay(quoteFor(accountId, kind), 140),
    async myListings(accountId) {
      return delay(
        db.listings.filter((l) => l.account_id === accountId).map((l) => ({
          ...l,
          documents: db.documents.filter((d) => d.listing_id === l.id),
          scheduled_price_changes: db.priceChanges.filter((p) => p.listing_id === l.id && !p.applied_at),
        })).sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at)),
      );
    },
    async saveListing(input) {
      requireAuth();
      const errs = validateListing(input);
      if (errs.length) fail('Some fields need attention.', '22023', errs);
      const make = S.MAKES.find((m) => m.id === input.make_id)?.name ?? null;
      const model = S.MODELS.find((m) => m.id === input.model_id)?.name ?? null;
      const reg = input.registration ? input.registration.toUpperCase().replace(/\s+/g, '') : null;
      if (input.id) {
        const l = db.listings.find((x) => x.id === input.id) ?? fail('Listing not found', 'not_found');
        const wasLive = ['active', 'paused', 'pending_sale'].includes(l.state);
        Object.assign(l, {
          ...input, make, model, registration: reg, images: input.images ?? l.images, attributes: input.attributes ?? l.attributes,
          custom_attributes: input.custom_attributes ?? l.custom_attributes, updated_at: now(),
        });
        l.quality_score = qualityScore(l);
        if (wasLive) { l.review_status = 'pending'; scheduleReviewAfterEdit(l.id); }
        return delay(l.id, 350);
      }
      const id = uuid();
      const l: T.MyListing = {
        id, account_id: input.account_id, kind: input.kind, title: input.title, description: input.description ?? null,
        make_id: input.make_id ?? null, model_id: input.model_id ?? null, make, model, year: input.year ?? null,
        serial_number: input.serial_number ?? null, registration: reg, registry_match: 'unchecked',
        price_cents: input.price_cents ?? null, price_on_request: input.price_on_request ?? false, currency: 'USD',
        condition: input.condition ?? 'used', location_ident: input.location_ident ?? null, location_state: input.location_state ?? null,
        location_country: input.location_country ?? 'US', images: input.images ?? [], attributes: input.attributes ?? {},
        custom_attributes: input.custom_attributes ?? [], state: 'draft', review_status: 'pending', review_reasons: [],
        reviewed_at: null, quality_score: 0, billing_basis: null, published_at: null, expires_at: null, sold_at: null,
        source: 'manual', external_id: null, slug: S.slugify(input.title, id), created_at: now(), updated_at: now(),
        stats: { views: 0, saves: 0, leads: 0, spend_cents: 0 },
      };
      l.quality_score = qualityScore(l);
      db.listings.unshift(l);
      return delay(id, 350);
    },
    async submitListing(id) {
      const l = db.listings.find((x) => x.id === id) ?? fail('Listing not found', 'not_found');
      const a = acct(l.account_id);
      const q = quoteFor(a.id, l.kind);
      const missing = missingFor(l);
      if (!a.card_ok) missing.push('card');
      if (missing.length) return delay({ state: l.state, basis: null, fee_cents_per_day: q.fee_cents_per_day, requires_card: true, missing }, 300);
      l.state = 'pending_review';
      l.review_status = 'pending';
      l.review_reasons = [];
      l.billing_basis = l.billing_basis === 'grandfathered' ? 'grandfathered' : q.basis;
      l.updated_at = now();
      scheduleReview(id);
      return delay({ state: l.state, basis: l.billing_basis, fee_cents_per_day: q.fee_cents_per_day, requires_card: true, missing: [] }, 400);
    },
    async setListingState(id, state) {
      const l = db.listings.find((x) => x.id === id) ?? fail('Listing not found', 'not_found');
      const allowed: Record<string, T.ListingState[]> = {
        active: ['paused', 'pending_sale', 'sold', 'removed'],
        paused: ['active', 'pending_sale', 'sold', 'removed'],
        pending_sale: ['active', 'sold', 'removed'],
        draft: ['removed'], rejected: ['removed'], expired: ['removed'], sold: ['removed'], pending_review: ['removed'],
      };
      if (!(allowed[l.state] ?? []).includes(state)) fail(`Can’t move a ${l.state.replace('_', ' ')} listing to ${state.replace('_', ' ')}.`, 'P0001');
      if (state === 'active' && l.review_status === 'rejected') fail('Fix the review issues and resubmit first.', 'P0001');
      l.state = state;
      if (state === 'sold') l.sold_at = now();
      l.updated_at = now();
      const c = db.campaigns.find((x) => x.kind === 'listing' && x.creatives.some((cr) => cr.listing_id === id));
      if (c) c.status = state === 'active' ? 'active' : state === 'paused' ? 'paused' : 'ended';
      return delay(undefined);
    },
    async comps(listingId) {
      const l = db.listings.find((x) => x.id === listingId) ?? fail('Listing not found', 'not_found');
      const base = l.price_cents ?? 100_000_000;
      const rnd = S.prng(listingId.length + (l.model_id ?? 1));
      const items = Array.from({ length: 6 }, (_, i) => {
        const sold = i % 3 === 0;
        return {
          id: S.mkId(900 + i, 'l'), title: `${(l.year ?? 2012) - 2 + i} ${l.make ?? ''} ${l.model ?? l.title.split(' — ')[0]}`.trim(),
          year: (l.year ?? 2012) - 2 + i, price_cents: Math.round(base * (0.82 + rnd() * 0.36)), state: (sold ? 'sold' : 'active') as T.ListingState,
          hours: Math.round(((l.attributes.airframe_total_hours as number) || 3000) * (0.6 + rnd() * 0.9)),
          days_on_market: Math.round(30 + rnd() * 160), sold_at: sold ? S.daysAgo(Math.round(rnd() * 200)) : null,
        };
      });
      const prices = items.map((i) => i.price_cents).sort((a, b) => a - b);
      const doms = items.map((i) => i.days_on_market).sort((a, b) => a - b);
      return delay({ listing_id: listingId, count: items.length, median_price_cents: prices[3], median_days_on_market: doms[3], items });
    },
    async uploadListingPhoto(original) {
      if (original.size > 40 * 1024 * 1024) fail('Photos must be under 40 MB.', 'upload');
      const file = await toJpeg(original);
      return delay(fakeUpload(file), 500);
    },
    async uploadListingDocument(listingId, file, kind, isPublic) {
      const doc: T.ListingDocument = { id: uuid(), listing_id: listingId, kind, path: fakeUpload(file).path, name: file.name, public: isPublic, created_at: now() };
      db.documents.push(doc);
      return delay(doc, 500);
    },
    async deleteListingDocument(docId) {
      db.documents = db.documents.filter((d) => d.id !== docId);
      return delay(undefined);
    },
    async schedulePriceChange(listingId, priceCents, effectiveAt) {
      if (Date.parse(effectiveAt) <= Date.now()) fail('Pick a date in the future.', '22023', [{ field: 'effective_at', message: 'Must be in the future' }]);
      const p: T.ScheduledPriceChange = { id: uuid(), listing_id: listingId, price_cents: priceCents, effective_at: effectiveAt, applied_at: null };
      db.priceChanges.push(p);
      return delay(p);
    },
    async cancelPriceChange(id) {
      db.priceChanges = db.priceChanges.filter((p) => p.id !== id);
      return delay(undefined);
    },

    // ── Import / feeds ──
    async importPreview(_accountId, csv) {
      const rows = parseCsv(csv);
      if (rows.length < 2) fail('That file has no data rows. The first row must be column headers.', 'import');
      const headers = rows[0].map((h) => h.trim());
      const sample = rows.slice(1, 6).map((r) => Object.fromEntries(headers.map((h, i) => [h, (r[i] ?? '').trim()])));
      return delay({ headers, sample_rows: sample, row_count: rows.length - 1, suggested_mapping: Object.fromEntries(headers.map((h) => [h, suggestHeader(h)])) }, 600);
    },
    async importRun(accountId, csv, mapping) {
      const rows = parseCsv(csv);
      const headers = rows[0].map((h) => h.trim());
      const run: T.ImportRun = { id: uuid(), account_id: accountId, feed_id: null, source: 'csv_upload', started_at: now(), finished_at: null, created: 0, updated: 0, unchanged: 0, removed: 0, failed: 0, errors: [] };
      rows.slice(1).forEach((r, i) => {
        const rec: Record<string, string> = {};
        headers.forEach((h, j) => { const k = mapping[h]; if (k) rec[k] = (r[j] ?? '').trim(); });
        const title = rec.title || [rec.year, rec.make, rec.model].filter(Boolean).join(' ');
        if (!title) { run.failed++; run.errors.push({ row: i + 2, message: 'No title, or year/make/model, to name the listing' }); return; }
        const priceRaw = rec.price?.replace(/[$,\s]/g, '');
        if (priceRaw && Number.isNaN(Number(priceRaw))) { run.failed++; run.errors.push({ row: i + 2, field: 'price', message: `“${rec.price}” is not a number` }); return; }
        const existing = rec.external_id ? db.listings.find((l) => l.account_id === accountId && l.external_id === rec.external_id) : undefined;
        const attrs: T.Attributes = {};
        for (const [k, v] of Object.entries(rec)) {
          const d = ATTRIBUTE_DEFS.find((x) => x.key === k);
          if (d && v) attrs[k] = ['number', 'integer', 'hours', 'money'].includes(d.data_type) ? Number(v.replace(/,/g, '')) : d.data_type === 'boolean' ? /^(y|yes|true|1)$/i.test(v) : v;
        }
        if (existing) { Object.assign(existing.attributes, attrs); existing.updated_at = now(); run.updated++; return; }
        const id = uuid();
        const make = S.MAKES.find((m) => m.name.toLowerCase().startsWith((rec.make ?? '').toLowerCase()) && rec.make);
        db.listings.unshift({
          ...S.LISTINGS[10], id, account_id: accountId, title, kind: (rec.kind as T.ListingKind) || 'helicopter', state: 'draft', review_status: 'pending',
          make_id: make?.id ?? null, make: make?.name ?? rec.make ?? null, model_id: null, model: rec.model ?? null, year: rec.year ? Number(rec.year) : null,
          price_cents: priceRaw ? Math.round(Number(priceRaw) * 100) : null, images: rec.images ? rec.images.split(/[|;\s]+/).filter(Boolean) : [],
          attributes: attrs, custom_attributes: [], source: 'csv', external_id: rec.external_id ?? null, slug: S.slugify(title, id),
          created_at: now(), updated_at: now(), stats: { views: 0, saves: 0, leads: 0, spend_cents: 0 }, quality_score: 40,
        });
        run.created++;
      });
      run.finished_at = now();
      db.runs.unshift(run);
      return delay(run, 900);
    },
    importRuns: (accountId) => delay(db.runs.filter((r) => r.account_id === accountId)),
    feeds: (accountId) => delay(db.feeds.filter((f) => f.account_id === accountId && f.active !== false)),
    async saveFeed(feed) {
      if (!/^https:\/\//.test(feed.url)) fail('Feed URL must start with https://', '22023', [{ field: 'url', message: 'Must be https' }]);
      if (feed.id) {
        const f = db.feeds.find((x) => x.id === feed.id) ?? fail('Feed not found', 'not_found');
        Object.assign(f, feed);
        return delay(f.id);
      }
      const f: T.Feed = { id: uuid(), mapping: {}, schedule: 'daily', active: true, last_run_at: null, last_status: null, last_error: null, created_at: now(), ...feed };
      db.feeds.unshift(f);
      return delay(f.id);
    },
    async deleteFeed(id) {
      const f = db.feeds.find((x) => x.id === id);
      if (f) f.active = false;
      return delay(undefined);
    },
    async runFeedNow(id) {
      const f = db.feeds.find((x) => x.id === id) ?? fail('Feed not found', 'not_found');
      const run: T.ImportRun = { id: uuid(), account_id: f.account_id, feed_id: id, source: 'feed', started_at: now(), finished_at: now(), created: 0, updated: 2, unchanged: 8, removed: 0, failed: 0, errors: [] };
      db.runs.unshift(run);
      f.last_run_at = now();
      f.last_status = 'ok';
      return delay(run, 1200);
    },

    // ── Leads ──
    async inbox(accountId, filter) {
      let rows = db.leads.filter((l) => l.account_id === accountId).map(inboxRow);
      if (filter.status?.length) rows = rows.filter((r) => filter.status!.includes(r.status));
      if (filter.assigned === 'me') rows = rows.filter((r) => r.assigned_to === db.me.id);
      else if (filter.assigned === 'unassigned') rows = rows.filter((r) => !r.assigned_to);
      else if (filter.assigned) rows = rows.filter((r) => r.assigned_to === filter.assigned);
      if (filter.listing_id) rows = rows.filter((r) => r.listing_id === filter.listing_id);
      if (filter.unread) rows = rows.filter((r) => r.unread > 0);
      if (filter.q) {
        const q = filter.q.toLowerCase();
        rows = rows.filter((r) => [r.buyer_name, r.buyer_email, r.listing_title, r.last_message].some((s) => s?.toLowerCase().includes(q)));
      }
      rows.sort((a, b) => Date.parse(b.last_activity_at) - Date.parse(a.last_activity_at));
      return delay(rows);
    },
    async leadThread(leadId) {
      const lead = db.leads.find((l) => l.id === leadId) ?? fail('Lead not found', 'not_found');
      const l = db.listings.find((x) => x.id === lead.listing_id);
      return delay({
        lead,
        listing: l ? { id: l.id, title: l.title, thumb: l.images[0] ?? null, price_cents: l.price_cents, state: l.state } : null,
        messages: db.messages.filter((m) => m.lead_id === leadId).sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at)),
      });
    },
    async sendMessage(leadId, body, attachments = []) {
      const text = body.trim();
      if (!text && !attachments.length) fail('Write a message first.', '22023');
      if (text.length > 8000) fail('Messages are limited to 8,000 characters.', '22023');
      const lead = db.leads.find((l) => l.id === leadId) ?? fail('Lead not found', 'not_found');
      const id = uuid();
      db.messages.push({
        id, lead_id: leadId, sender_id: db.me.id, sender_side: 'seller', sender_name: db.me.display_name, body: text, attachments,
        via: 'web', flags: [], created_at: now(), read_by_buyer_at: null, read_by_seller_at: now(),
      });
      lead.last_activity_at = now();
      if (!lead.first_response_at) lead.first_response_at = now();
      if (lead.status === 'new') lead.status = 'contacted';
      emitLead(lead.account_id, leadId, 'message');
      return delay(id, 250);
    },
    async markRead(leadId) {
      for (const m of db.messages) if (m.lead_id === leadId && m.sender_side === 'buyer' && !m.read_by_seller_at) m.read_by_seller_at = now();
      return delay(undefined, 80);
    },
    async updateLead(leadId, patch) {
      const lead = db.leads.find((l) => l.id === leadId) ?? fail('Lead not found', 'not_found');
      Object.assign(lead, patch);
      lead.last_activity_at = patch.status || patch.assigned_to !== undefined ? now() : lead.last_activity_at;
      emitLead(lead.account_id, leadId, 'lead');
      return delay(undefined, 150);
    },
    async uploadLeadAttachment(_leadId, file) {
      if (file.size > 20 * 1024 * 1024) fail('Attachments must be under 20 MB.', 'upload');
      const u = fakeUpload(file);
      return delay({ path: u.path, url: u.url, name: file.name, mime: file.type || 'application/octet-stream', size: file.size }, 400);
    },
    savedReplies: (accountId) => delay(db.replies.filter((r) => r.account_id === accountId)),
    async saveSavedReply(reply) {
      if (!reply.title.trim() || !reply.body.trim()) fail('Give the reply a title and some text.', '22023');
      if (reply.id) {
        const r = db.replies.find((x) => x.id === reply.id) ?? fail('Not found', 'not_found');
        Object.assign(r, reply);
        return delay(r.id);
      }
      const id = uuid();
      db.replies.push({ id, created_by: db.me.id, ...reply });
      return delay(id);
    },
    async deleteSavedReply(id) {
      db.replies = db.replies.filter((r) => r.id !== id);
      return delay(undefined);
    },
    subscribeLeads(accountId, onChange) {
      const set = leadListeners.get(accountId) ?? new Set();
      set.add(onChange);
      leadListeners.set(accountId, set);
      let timer: ReturnType<typeof setTimeout> | null = null;
      if (!simulatedInbound && accountId === S.A_SUMMIT) {
        timer = setTimeout(() => {
          simulatedInbound = true;
          const lead = db.leads.find((l) => l.id === S.mkId(3, 'e'));
          if (!lead) return;
          db.messages.push({
            id: uuid(), lead_id: lead.id, sender_id: lead.buyer_id, sender_side: 'buyer', sender_name: lead.buyer_name, via: 'app',
            body: 'Also — any chance you’d consider a trade for a 206L3? Clean, 8,900 TT.', attachments: [], flags: [],
            created_at: now(), read_by_buyer_at: now(), read_by_seller_at: null,
          });
          lead.last_activity_at = now();
          emitLead(accountId, lead.id, 'message');
        }, 25_000);
      }
      return () => {
        set.delete(onChange);
        if (timer) clearTimeout(timer);
      };
    },

    // ── Campaigns ──
    campaigns: (accountId) => delay(db.campaigns.filter((c) => c.account_id === accountId)),
    async campaign(id) {
      return delay(db.campaigns.find((c) => c.id === id) ?? fail('Campaign not found', 'not_found'));
    },
    async saveCampaign(input) {
      const p = policy();
      const errs: T.FieldError[] = [];
      if (!input.name.trim()) errs.push({ field: 'name', message: 'Name your campaign' });
      if (input.daily_budget_cents != null && input.daily_budget_cents < p.min_daily_budget_cents) errs.push({ field: 'daily_budget_cents', message: `Daily budget must be at least $${(p.min_daily_budget_cents / 100).toFixed(2)}` });
      if (input.daily_budget_cents == null && input.lifetime_budget_cents == null) errs.push({ field: 'daily_budget_cents', message: 'Set a daily or lifetime budget' });
      if (!input.placements.length) errs.push({ field: 'placements', message: 'Choose at least one placement' });
      if (errs.length) fail('Some fields need attention.', '22023', errs);
      const pricing = input.kind === 'brand' ? { model: 'cpm' as const, rate_cents: p.brand_cpm_cents } : input.objective === 'views' ? { model: 'cpm' as const, rate_cents: p.promoted_cpm_cents } : { model: 'cpc' as const, rate_cents: p.promoted_cpc_cents };
      if (input.id) {
        const c = db.campaigns.find((x) => x.id === input.id) ?? fail('Campaign not found', 'not_found');
        Object.assign(c, input, { updated_at: now() });
        return delay(c.id);
      }
      const id = uuid();
      db.campaigns.unshift({
        ...input, id, status: 'draft', pause_reason: null, pricing, spent_today_cents: 0, spent_total_cents: 0, spend_day: null,
        created_by: db.me.id, created_at: now(), updated_at: now(), creatives: [],
      });
      return delay(id, 300);
    },
    async saveCreative(input) {
      const c = db.campaigns.find((x) => x.id === input.campaign_id) ?? fail('Campaign not found', 'not_found');
      if (input.kind === 'brand') {
        const errs: T.FieldError[] = [];
        if (!input.headline?.trim()) errs.push({ field: 'headline', message: 'Headline is required' });
        if ((input.headline ?? '').length > 60) errs.push({ field: 'headline', message: 'Keep the headline under 60 characters' });
        if (!input.target_url || !/^https:\/\//.test(input.target_url)) errs.push({ field: 'target_url', message: 'Link must start with https://' });
        if (errs.length) fail('Some fields need attention.', '22023', errs);
      }
      const l = input.listing_id ? db.listings.find((x) => x.id === input.listing_id) : null;
      const existing = input.id ? c.creatives.find((x) => x.id === input.id) : undefined;
      const cr: T.Creative = {
        id: existing?.id ?? uuid(), campaign_id: c.id, kind: input.kind, listing_id: input.listing_id ?? null, headline: input.headline ?? null,
        body: input.body ?? null, image_url: input.image_url ?? null, target_url: input.target_url ?? null, cta_label: input.cta_label ?? null,
        review_status: l ? l.review_status : 'pending', review_reasons: l?.review_reasons ?? [],
      };
      if (existing) Object.assign(existing, cr); else c.creatives.push(cr);
      if (cr.review_status === 'pending') setTimeout(() => { cr.review_status = 'approved'; }, 2000);
      return delay(cr.id);
    },
    async deleteCreative(id) {
      const c = db.campaigns.find((x) => x.creatives.some((cr) => cr.id === id)) ?? fail('Creative not found', 'not_found');
      if ((c.status === 'active' || c.status === 'budget_exhausted') && c.creatives.length <= 1) {
        fail('Pause the campaign before removing its last creative.', '22023');
      }
      c.creatives = c.creatives.filter((cr) => cr.id !== id);
      return delay(undefined);
    },
    async setCampaignStatus(id, status) {
      const c = db.campaigns.find((x) => x.id === id) ?? fail('Campaign not found', 'not_found');
      const a = acct(c.account_id);
      const p = policy();
      if (status === 'active') {
        const errors: { code: string; message: string }[] = [];
        if (!a.card_ok) errors.push({ code: 'card_required', message: 'Add a card on file before launching.' });
        if (a.status !== 'active') errors.push({ code: 'account_inactive', message: 'This account is suspended.' });
        if (!c.creatives.length) errors.push({ code: 'no_creatives', message: 'Add at least one listing or brand creative.' });
        if (c.kind === 'promotion' && c.creatives.some((cr) => cr.review_status === 'limited')) errors.push({ code: 'limited_not_promotable', message: 'One or more listings has limited visibility and can’t be promoted. Fix its review issues or remove it.' });
        if (c.creatives.some((cr) => cr.review_status === 'rejected')) errors.push({ code: 'creative_rejected', message: 'A creative was rejected. Edit it and resubmit.' });
        const est = audienceFor(c.targeting);
        if (hasPersonal(c.targeting) && est < p.min_audience) errors.push({ code: 'audience_too_small', message: `Audience is about ${est}; the minimum is ${p.min_audience}. Broaden targeting.` });
        const bal = balances(a.id);
        if (bal.paid + bal.promo <= 0 && !a.auto_recharge_enabled) errors.push({ code: 'no_funds', message: 'Add funds or turn on auto-recharge.' });
        if (errors.length) return delay({ ok: false, status: c.status, errors }, 300);
      }
      c.status = status;
      c.pause_reason = status === 'paused' ? `Paused by ${db.me.display_name}` : null;
      c.updated_at = now();
      return delay({ ok: true, status: c.status }, 300);
    },
    async estimateAudience(t) {
      const estimate = audienceFor(t);
      return delay({ estimate, meets_minimum: !hasPersonal(t) || estimate >= policy().min_audience }, 260);
    },
    async campaignStats(accountId, from, to) {
      const ids = new Set(db.campaigns.filter((c) => c.account_id === accountId).map((c) => c.id));
      const days = db.stats.filter((s) => ids.has(s.campaign_id) && s.day >= from && s.day <= to);
      const campaigns = db.campaigns.filter((c) => ids.has(c.id)).map((c) => {
        const ds = days.filter((d) => d.campaign_id === c.id);
        return {
          campaign_id: c.id, name: c.name, kind: c.kind, day: '',
          impressions: ds.reduce((a, d) => a + d.impressions, 0), clicks: ds.reduce((a, d) => a + d.clicks, 0),
          leads: ds.reduce((a, d) => a + d.leads, 0), cost_cents: ds.reduce((a, d) => a + d.cost_cents, 0),
        };
      });
      return delay({ from, to, campaigns, days });
    },
    async dailyActivity(accountId, from, to) {
      const out: T.DailyActivity[] = [];
      const isSummit = accountId === S.A_SUMMIT;
      const ids = new Set(db.campaigns.filter((c) => c.account_id === accountId).map((c) => c.id));
      for (let d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`); d = new Date(d.getTime() + 86400000)) {
        const day = d.toISOString().slice(0, 10);
        const o = db.organic.find((x) => x.day === day);
        const st = db.stats.filter((s) => s.day === day && ids.has(s.campaign_id));
        out.push({
          day,
          views: isSummit ? (o?.views ?? 0) : 0,
          saves: isSummit ? (o?.saves ?? 0) : 0,
          leads: db.leads.filter((l) => l.account_id === accountId && l.created_at.slice(0, 10) === day).length,
          impressions: st.reduce((a, s) => a + s.impressions, 0),
          clicks: st.reduce((a, s) => a + s.clicks, 0),
          spend_cents: st.reduce((a, s) => a + s.cost_cents, 0),
        });
      }
      return delay(out);
    },
    currentPolicy: () => delay(policy(), 100),
    async uploadCreativeImage(_accountId, original) {
      const file = await toJpeg(original);
      return delay(fakeUpload(file), 400);
    },

    // ── Billing ──
    async billingSummary(accountId) {
      const a = acct(accountId);
      const b = balances(accountId);
      const today = new Date().toISOString().slice(0, 10);
      const spentToday = -db.ledger.filter((r) => r.account_id === accountId && r.entry_type === 'spend' && r.occurred_at.slice(0, 10) === today).reduce((x, r) => x + r.amount_cents, 0)
        || db.campaigns.filter((c) => c.account_id === accountId && c.status === 'active').reduce((x, c) => x + c.spent_today_cents, 0);
      return delay({
        account_id: accountId, paid_cents: b.paid, promo_cents: b.promo, promo_next_expiry: b.next,
        grants: db.grants.filter((g) => g.account_id === accountId).sort((x, y) => Date.parse(x.expires_at) - Date.parse(y.expires_at)),
        card: { brand: a.card_brand, last4: a.card_last4, ok: a.card_ok },
        auto_recharge: { enabled: a.auto_recharge_enabled, threshold_cents: a.auto_recharge_threshold_cents, amount_cents: a.auto_recharge_amount_cents },
        today: { spent_cents: spentToday, cap_cents: a.daily_spend_cap_cents },
        ledger: db.ledger.filter((r) => r.account_id === accountId).sort((x, y) => Date.parse(y.occurred_at) - Date.parse(x.occurred_at)).slice(0, 50),
      });
    },
    ledger: (accountId, from, to) =>
      delay(db.ledger.filter((r) => r.account_id === accountId && (!from || r.occurred_at >= from) && (!to || r.occurred_at.slice(0, 10) <= to)).sort((x, y) => Date.parse(y.occurred_at) - Date.parse(x.occurred_at))),
    async redeemCode(accountId, code) {
      await delay(null, 400);
      const a = acct(accountId);
      const p = db.programs.find((x) => x.code && x.code.toLowerCase() === code.trim().toLowerCase());
      if (!p) return { ok: false, message: 'That code isn’t valid. Check for typos — codes aren’t case-sensitive.' };
      if (!p.active) return { ok: false, message: 'That promotion has ended.' };
      if (p.requires_card && !a.card_ok) return { ok: false, message: 'Add a card on file first — this credit requires one (you won’t be charged).' };
      if (p.max_redemptions != null && (p.redemptions ?? 0) >= p.max_redemptions) return { ok: false, message: 'This code has been fully redeemed.' };
      if (db.grants.filter((g) => g.account_id === accountId && g.program_id === p.id).length >= p.per_account_limit) return { ok: false, message: 'You’ve already redeemed this code.' };
      const g: T.CreditGrant = {
        id: uuid(), program_id: p.id, program_name: p.name, account_id: accountId, account_name: a.display_name, amount_cents: p.amount_cents,
        remaining_cents: p.amount_cents, applies_to: p.applies_to, expires_at: new Date(Date.now() + p.expires_after_days * 86400000).toISOString(),
        granted_at: now(), granted_by: null, note: `Redeemed code ${p.code}`,
      };
      db.grants.push(g);
      p.redemptions = (p.redemptions ?? 0) + 1;
      db.ledger.push({ id: db.ledger.length + 1, account_id: accountId, occurred_at: now(), entry_type: 'credit_grant', funding: 'promo', amount_cents: p.amount_cents, purpose: null, grant_id: g.id, campaign_id: null, listing_id: null, stripe_ref: null, memo: `Code ${p.code}` });
      return { ok: true, grant_id: g.id, amount_cents: g.amount_cents, expires_at: g.expires_at };
    },
    setupCard: () => delay({ client_secret: `mock_seti_${uuid()}` }, 300),
    async confirmMockCard(accountId, card) {
      const a = acct(accountId);
      a.card_ok = true;
      a.card_brand = card.brand;
      a.card_last4 = card.last4;
      a.default_payment_method_id = `pm_mock_${card.last4}`;
      return delay(undefined, 600);
    },
    async listCards(accountId) {
      const a = acct(accountId);
      if (!a.card_ok) return delay([]);
      const cards: T.Card[] = [{ id: a.default_payment_method_id ?? 'pm_mock', brand: a.card_brand ?? 'visa', last4: a.card_last4 ?? '4242', exp_month: 8, exp_year: 2029, is_default: true }];
      if (accountId === S.A_SUMMIT) cards.push({ id: 'pm_mock_amex', brand: 'amex', last4: '8431', exp_month: 2, exp_year: 2028, is_default: false });
      return delay(cards);
    },
    async setDefaultCard(accountId, pm) {
      const a = acct(accountId);
      a.default_payment_method_id = pm;
      if (pm === 'pm_mock_amex') { a.card_brand = 'amex'; a.card_last4 = '8431'; }
      else if (pm === 'pm_mock_visa') { a.card_brand = 'visa'; a.card_last4 = '4242'; }
      return delay(undefined);
    },
    async topup(_accountId, amount) {
      if (amount < 2500) fail('Minimum top-up is $25.', '22023');
      if (amount > 5_000_000) fail('Maximum top-up is $50,000. Contact us for wire transfer.', '22023');
      return delay({ client_secret: `mock_pi_${uuid()}` }, 300);
    },
    async confirmMockTopup(accountId, amount) {
      db.ledger.push({ id: db.ledger.length + 1, account_id: accountId, occurred_at: now(), entry_type: 'topup', funding: 'paid', amount_cents: amount, purpose: null, grant_id: null, campaign_id: null, listing_id: null, stripe_ref: `pi_mock_${uuid().slice(0, 8)}`, memo: 'Top-up' });
      return delay(undefined, 700);
    },
    identityStart: () => delay({ url: 'https://verify.stripe.com/start/test_mock' }),
    portalLink: () => delay({ url: 'https://billing.stripe.com/p/session/test_mock' }),

    // ── Settings ──
    apiKeys: (accountId) => delay(db.apiKeys.filter((k) => k.account_id === accountId)),
    async createApiKey(accountId, name) {
      if (!name.trim()) fail('Name the key so you know where it’s used.', '22023', [{ field: 'name', message: 'Required' }]);
      const raw = Array.from(crypto.getRandomValues(new Uint8Array(24)), (b) => b.toString(16).padStart(2, '0')).join('');
      const key = `fk_live_${raw}`;
      const k: T.ApiKey = { id: uuid(), account_id: accountId, name, last4: key.slice(-4), created_at: now(), revoked_at: null };
      db.apiKeys.unshift(k);
      return delay({ id: k.id, key, last4: k.last4 });
    },
    async revokeApiKey(id) {
      const k = db.apiKeys.find((x) => x.id === id);
      if (k) k.revoked_at = now();
      return delay(undefined);
    },
    webhooks: (accountId) => delay(db.webhooks.filter((w) => w.account_id === accountId)),
    async createWebhook(accountId, url, events) {
      if (!/^https:\/\//.test(url)) fail('Webhook URL must start with https://', '22023', [{ field: 'url', message: 'Must be https' }]);
      if (!events.length) fail('Pick at least one event.', '22023', [{ field: 'events', message: 'Required' }]);
      const w: T.Webhook = { id: uuid(), account_id: accountId, url, events, active: true, created_at: now() };
      db.webhooks.unshift(w);
      const secret = `whsec_${Array.from(crypto.getRandomValues(new Uint8Array(18)), (b) => b.toString(16).padStart(2, '0')).join('')}`;
      return delay({ id: w.id, secret });
    },
    async deleteWebhook(id) {
      db.webhooks = db.webhooks.filter((w) => w.id !== id);
      return delay(undefined);
    },

    // ── Staff ──
    isStaff: () => delay(db.me.is_staff, 60),
    async adminOverview(from, to) {
      const inRange = (iso: string) => iso.slice(0, 10) >= from && iso.slice(0, 10) <= to;
      const spend = db.ledger.filter((r) => r.entry_type === 'spend' && inRange(r.occurred_at));
      const daily: T.AdminOverview['daily'] = [];
      for (let d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`); d = new Date(d.getTime() + 86400000)) {
        const day = d.toISOString().slice(0, 10);
        const ds = spend.filter((r) => r.occurred_at.slice(0, 10) === day);
        daily.push({
          day,
          paid_cents: -ds.filter((r) => r.funding === 'paid').reduce((a, r) => a + r.amount_cents, 0) + (day.charCodeAt(9) % 4) * 900,
          promo_cents: -ds.filter((r) => r.funding === 'promo').reduce((a, r) => a + r.amount_cents, 0) + 1800,
          leads: db.leads.filter((l) => l.created_at.slice(0, 10) === day).length + (day.charCodeAt(9) % 3),
        });
      }
      const top = db.campaigns.filter((c) => c.kind !== 'listing').map((c) => {
        const st = db.stats.filter((s) => s.campaign_id === c.id && s.day >= from && s.day <= to);
        return { campaign_id: c.id, name: c.name, account_name: acct(c.account_id).display_name, spend_cents: st.reduce((a, s) => a + s.cost_cents, 0), clicks: st.reduce((a, s) => a + s.clicks, 0), leads: st.reduce((a, s) => a + s.leads, 0) };
      });
      top.push({ campaign_id: S.mkId(90, 'k'), name: 'RotorTrade — Bell 407 fleet', account_name: 'RotorTrade', spend_cents: 61240, clicks: 402, leads: 11 });
      return delay({
        from, to,
        revenue: { paid_cents: daily.reduce((a, d) => a + d.paid_cents, 0), promo_cents: daily.reduce((a, d) => a + d.promo_cents, 0) },
        active_listings: db.listings.filter((l) => l.state === 'active').length + 31,
        accounts: {
          total: db.accounts.length, dealers: db.accounts.filter((a) => a.kind === 'dealer').length,
          individuals: db.accounts.filter((a) => a.kind === 'individual').length, suspended: db.accounts.filter((a) => a.status === 'suspended').length,
        },
        leads: daily.reduce((a, d) => a + d.leads, 0),
        top_campaigns: top.sort((a, b) => b.spend_cents - a.spend_cents).slice(0, 8),
        daily,
      });
    },
    adminPolicies: () => delay([...db.policies].sort((a, b) => Date.parse(b.effective_at) - Date.parse(a.effective_at))),
    async adminSetPolicy(p) {
      if (!db.me.is_staff) fail('Staff only', '42501');
      const id = uuid();
      db.policies.push({ ...p, id, created_by: db.me.id });
      audit('policy.create', 'pricing_policy', id, { mode: p.mode, effective_at: p.effective_at, note: p.note });
      return delay(id, 400);
    },
    adminPrograms: () => delay(db.programs),
    async adminCreateProgram(p) {
      if (p.code && db.programs.some((x) => x.code?.toLowerCase() === p.code!.toLowerCase())) fail('That code already exists.', '23505', [{ field: 'code', message: 'Taken' }]);
      if (p.amount_cents <= 0) fail('Amount must be positive.', '22023', [{ field: 'amount_cents', message: 'Must be > 0' }]);
      const id = uuid();
      db.programs.unshift({ ...p, id, created_by: db.me.id, created_at: now(), redemptions: 0 });
      audit('program.create', 'credit_program', id, { name: p.name, code: p.code, amount_cents: p.amount_cents });
      return delay(id);
    },
    adminGrants: () => delay([...db.grants].sort((a, b) => Date.parse(b.granted_at) - Date.parse(a.granted_at))),
    async adminGrantCredit(i) {
      const a = acct(i.account_id);
      if (i.amount_cents <= 0) fail('Amount must be positive.', '22023');
      const prog = db.programs.find((p) => p.id === i.program_id);
      const g: T.CreditGrant = {
        id: uuid(), program_id: i.program_id ?? null, program_name: prog?.name ?? null, account_id: a.id, account_name: a.display_name,
        amount_cents: i.amount_cents, remaining_cents: i.amount_cents, applies_to: i.applies_to,
        expires_at: new Date(Date.now() + i.expires_days * 86400000).toISOString(), granted_at: now(), granted_by: db.me.id, note: i.note,
      };
      db.grants.push(g);
      db.ledger.push({ id: db.ledger.length + 1, account_id: a.id, occurred_at: now(), entry_type: 'credit_grant', funding: 'promo', amount_cents: i.amount_cents, purpose: null, grant_id: g.id, campaign_id: null, listing_id: null, stripe_ref: null, memo: prog?.name ?? i.note });
      audit('credit.grant', 'account', a.id, { amount_cents: i.amount_cents, expires_days: i.expires_days, program: prog?.name ?? null, note: i.note });
      return delay(g.id, 400);
    },
    async adminAccounts(q) {
      const rows = db.accounts.filter((a) => !q || a.display_name.toLowerCase().includes(q.toLowerCase()) || a.slug.includes(q.toLowerCase())).map((a) => {
        const b = balances(a.id);
        return {
          ...a, active_listings: db.listings.filter((l) => l.account_id === a.id && l.state === 'active').length + (a.id === S.A_RT ? 14 : a.id === S.A_HIGHDESERT ? 5 : 0),
          paid_cents: b.paid + (a.id === S.A_RT ? 31250 : 0), promo_cents: b.promo, lifetime_paid_cents: a.id === S.A_SUMMIT ? 50000 : a.id === S.A_RT ? 125000 : a.card_ok ? 2500 : 0,
        };
      });
      return delay(rows);
    },
    async adminSetAccount(accountId, patch) {
      const a = acct(accountId);
      const before = { status: a.status, trust_tier: a.trust_tier, verified: a.verified };
      if (patch.trust_tier != null) {
        a.trust_tier = patch.trust_tier;
        a.daily_spend_cap_cents = policy().tier_daily_caps_cents[String(patch.trust_tier)] ?? a.daily_spend_cap_cents;
      }
      if (patch.status) a.status = patch.status;
      if (patch.verified != null) a.verified = patch.verified;
      audit('account.set', 'account', accountId, { before, after: patch });
      return delay(undefined, 300);
    },
    adminReviewRuns: (f) => delay(db.reviewRuns.filter((r) => (!f?.decision || r.decision === f.decision) && (!f?.target_type || r.target_type === f.target_type))),
    async adminOverrideReview(targetType, id, decision, reason) {
      if (!reason.trim()) fail('Give a reason — it’s written to the audit log and shown to the seller.', '22023', [{ field: 'reason', message: 'Required' }]);
      if (targetType === 'listing') {
        const l = db.listings.find((x) => x.id === id);
        if (l) {
          l.review_status = decision;
          l.review_reasons = decision === 'approved' ? [] : [{ code: 'staff_override', message: reason }];
          if (decision === 'approved' || decision === 'limited') { if (['rejected', 'pending_review'].includes(l.state)) l.state = 'active'; }
          if (decision === 'rejected') l.state = 'rejected';
        }
      }
      db.reviewRuns.unshift({
        id: Math.max(0, ...db.reviewRuns.map((r) => r.id)) + 1, target_type: targetType, target_id: id,
        target_label: db.reviewRuns.find((r) => r.target_id === id)?.target_label ?? id, account_name: db.reviewRuns.find((r) => r.target_id === id)?.account_name ?? null,
        checks: { override: true, by: db.me.display_name, reason }, decision, model: null, occurred_at: now(),
      });
      audit('review.override', targetType, id, { decision, reason });
      return delay(undefined, 300);
    },
    adminAuditLog: (f) => delay(db.audit.filter((a) => !f?.action || a.action.includes(f.action))),
  };
}

function scheduleReviewAfterEdit(id: string) {
  setTimeout(() => {
    const l = db.listings.find((x) => x.id === id);
    if (l && l.review_status === 'pending' && l.state !== 'pending_review') {
      l.review_status = 'approved';
      l.reviewed_at = now();
    }
  }, 3000);
}
